import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import Fetch from "../../services/Fetch";
import RequestDetails from "../../popup/RequestDetails";
import SnackbarAlert from "../../components/SnackBar";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import RequestFilter from "../../popup/RequestsFilter";
import { useConstants } from "../../hooks/UseConstants";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import { useRequestsFilter } from "../../filter/UseRequestsFilter";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearch } from "../../hooks/UseSearch";
import { usePagination } from "../../hooks/UsePagination";
import AdminPageHero from "../../components/AdminPageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import PageContent from "../../components/PageContent";
import AdminListToolbar from "../../components/AdminListToolbar";
import ModalOverlay from "../../components/ModalOverlay";

function Requests() {
    const intl = useIntl();
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const { search, setSearch, order, setOrder } = useSearch();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait, filterWait, setFilterWait } = useWaits();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const { status, setStatus, from, setFrom, to, setTo, value, setValue, teacherId, setTeacherId } = useRequestsFilter();
    const [request, setRequest] = useState(null);
    const [coursesRequests, setCoursesRequests] = useState([]);
    const [courseId, setCourseId] = useState(null);
    const [operation, setOperation] = useState(null);
    const [requestCounts, setRequestCounts] = useState(0);
    const [courseName, setCourseName] = useState("");

    const setFilterStatus = (nextStatus) => {
        setStatus((prevStatus) => (
            prevStatus.includes(nextStatus)
                ? prevStatus.filter((item) => nextStatus !== item)
                : [...prevStatus, nextStatus]
        ));
    };

    const buildCoursesUrl = () => {
        const params = new URLSearchParams();
        params.set("page", String(page + 1));

        if (order) {
            order.split("&").forEach((part) => {
                const [key, val] = part.split("=");
                if (key && val !== undefined) params.set(key, val);
            });
        }

        const searchTerm = (courseName || search || "").trim();
        if (searchTerm) params.set("search", searchTerm);

        status.forEach((item) => params.append("status[]", item));

        if (from && to) {
            params.set("from", from);
            params.set("to", to);
        }

        if (teacherId) params.set("teacher_id", teacherId);

        return `${host}/courses?${params.toString()}`;
    };

    const getCoursesRequests = async () => {
        const result = await Fetch(buildCoursesUrl(), "GET", null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            const sorted = [...items].sort((a, b) => (
                a.status === "pending" ? -1 : b.status === "pending" ? 1 : 0
            ));
            setCoursesRequests(sorted);
            setTotalPages(pagination.last_page);
            setRequestCounts(pagination.total);
            setCurrentPage(page);
        } else {
            setCoursesRequests([]);
            setTotalPages(1);
            setRequestCounts(0);
        }

        setGetWait(false);
    };

    const requestsFiltering = async () => {
        const result = await Fetch(buildCoursesUrl(), "GET", null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            const sorted = [...items].sort((a, b) => (
                a.status === "pending" ? -1 : b.status === "pending" ? 1 : 0
            ));
            setCoursesRequests(sorted);
            setTotalPages(pagination.last_page);
            setRequestCounts(pagination.total);
            setCurrentPage(page);
            setPopup("filter", "none");
        }

        setFilterWait(false);
    };

    const getCourseDetails = async (id) => {
        const result = await Fetch(`${host}/courses/${id}/show`, 'GET', null);
        if (result.status === 200) {
            setRequest(result.data.data);
        } else {
            setRequest(coursesRequests.find((item) => item.id === id) || null);
        }
        setPopup("details", "flex");
    };

    const changeCourseStatus = async (targetCourseId, nextStatus) => {
        setCourseId(targetCourseId);
        setOperation(nextStatus);
        setSendWait(true);
        const formData = new FormData();
        formData.append("new_status", nextStatus);
        const result = await Fetch(`${host}/admin/courses/${targetCourseId}/change-status`, "POST", formData);
        if (result.status === 200) {
            getCoursesRequests();
            setSnackBar("success", nextStatus === "accepted" ? <FormattedMessage id="approved_msg" /> : <FormattedMessage id="rejected_msg" />);
            setCourseId(null);
        } else {
            setSnackBar("error", result.data?.errors?.[0] || result.data?.message || "Error");
        }
        setSendWait(false);
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            getCoursesRequests();
        }
    }, [wait, page, search, order]);

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <AdminPageHero />

                <DashboardPanelCard
                    title={isAr ? "طلبات اعتماد الدورات" : "Course approval requests"}
                    subtitle={isAr ? "راجع طلبات النشر، وافتح التفاصيل، ثم وافق أو ارفض بسرعة من نفس الجدول." : "Review publishing requests, inspect details, and approve or reject quickly from one table."}
                    className="mx-0 !mt-0"
                >
                    <AdminListToolbar
                        isAr={isAr}
                        searchPlaceholder={intl.formatMessage({ id: "search_course_teacher_name" })}
                        search={search}
                        onSearchChange={setSearch}
                        order={order}
                        onOrderChange={setOrder}
                        totalLabel={intl.formatMessage({ id: "total_requests" })}
                        totalCount={requestCounts}
                        onFilterClick={() => setPopup("filter", "flex")}
                        orderOptions={[
                            { value: "", label: intl.formatMessage({ id: "date" }) },
                            { value: language === "en" ? "order_by=name_en&direction=asc" : "order_by=name_ar&direction=asc", label: intl.formatMessage({ id: "course_name" }) },
                            { value: "order_by=teacher.name&direction=asc", label: intl.formatMessage({ id: "teacher_name" }) },
                        ]}
                    />

                    <TableContainer component={Paper} dir={language === "en" ? "ltr" : "rtl"} sx={{ boxShadow: "none", borderRadius: 3 }}>
                        <Table sx={{ minWidth: 700 }} aria-label="requests table">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc" }}>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="request_id" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="course_name" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="teacher_name" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="request_date" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="status" /></StyledTableCell>
                                    <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="procedures" /></StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {coursesRequests.length > 0 ? coursesRequests.map((item) => {
                                    const isResolved = item.status === "rejected" || item.status === "accepted";
                                    return (
                                        <StyledTableRow key={item.id}>
                                            <StyledTableCell
                                                align={language === "en" ? "left" : "right"}
                                                component="th"
                                                scope="row"
                                                sx={{ cursor: isResolved ? "default" : "pointer" }}
                                                onClick={() => {
                                                    if (!isResolved) getCourseDetails(item.id);
                                                }}
                                            >
                                                {item.id}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                {language === "en" ? item.name_en : item.name_ar}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                <Box className="flex flex-row-reverse items-center justify-end">
                                                    <Box className="mx-2">{item.teacher.first_name} {item.teacher.last_name}</Box>
                                                    <Box className="w-7 h-7 rounded-full bg-gray-300 flex justify-center items-center font-bold">
                                                        {item.teacher.first_name.charAt(0)}
                                                    </Box>
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                {item.created_at.split(" ")[0]}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                <Chip
                                                    label={
                                                        item.status === "accepted"
                                                            ? intl.formatMessage({ id: "approved" })
                                                            : item.status === "rejected"
                                                                ? intl.formatMessage({ id: "rejected" })
                                                                : intl.formatMessage({ id: "wait_approval" })
                                                    }
                                                    color={item.status === "accepted" ? "success" : item.status === "rejected" ? "error" : "warning"}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: language === "en" ? "flex-start" : "flex-end" }}>
                                                    <Button
                                                        disabled={isResolved}
                                                        onClick={() => changeCourseStatus(item.id, "rejected")}
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: isResolved ? "#F2F2F2 !important" : undefined,
                                                            color: isResolved ? "#666666 !important" : undefined,
                                                        }}
                                                        className="h-8 !bg-red-300 !text-red-600 !font-bold hover:!bg-red-600 hover:!text-white"
                                                    >
                                                        {sendWait && courseId === item.id && operation === "rejected"
                                                            ? <CircularProgress size={20} color="white" />
                                                            : <FormattedMessage id="reject" />}
                                                    </Button>
                                                    <Button
                                                        disabled={isResolved}
                                                        onClick={() => changeCourseStatus(item.id, "accepted")}
                                                        variant="contained"
                                                        sx={{
                                                            backgroundColor: isResolved ? "#F2F2F2 !important" : undefined,
                                                            color: isResolved ? "#666666 !important" : undefined,
                                                        }}
                                                        className="h-8 !text-green-600 !bg-green-300 !font-bold hover:!bg-green-600 hover:!text-white"
                                                    >
                                                        {sendWait && courseId === item.id && operation === "accepted"
                                                            ? <CircularProgress size={20} color="white" />
                                                            : <FormattedMessage id="approve" />}
                                                    </Button>
                                                </Box>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    );
                                }) : (
                                    <StyledTableRow>
                                        <StyledTableCell colSpan={6} align="center">
                                            <Typography color="text.secondary" sx={{ py: 4 }}>
                                                {isAr ? "لا توجد طلبات مطابقة للبحث أو التصفية الحالية." : "No requests match the current search or filters."}
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }} dir="rtl">
                        <Button disabled={page + 1 === totalPages || totalPages === 0} onClick={() => setPage(currentPage + 1)}>
                            <NavigateNextIcon fontSize="large" />
                        </Button>
                        <Typography variant="body1" className="!text-xl" dir="ltr">
                            {totalPages ? currentPage + 1 : 0} / {totalPages || 0}
                        </Typography>
                        <Button disabled={page + 1 === 1} onClick={() => setPage(currentPage - 1)}>
                            <NavigateBeforeIcon fontSize="large" />
                        </Button>
                    </Box>
                </DashboardPanelCard>
            </PageContent>

            <ModalOverlay id="details" onClose={() => setPopup("details", "none")}>
                {request ? (
                    <RequestDetails onClickClose={() => setPopup("details", "none")} onClickAccept={changeCourseStatus} onClickReject={changeCourseStatus} request={request} />
                ) : null}
            </ModalOverlay>

            <ModalOverlay id="filter" onClose={() => setPopup("filter", "none")}>
                <RequestFilter
                    filterWait={filterWait}
                    setFilterWait={setFilterWait}
                    courseName={courseName}
                    setCourseName={setCourseName}
                    value={value}
                    setValue={setValue}
                    setFrom={setFrom}
                    setTo={setTo}
                    from={from}
                    to={to}
                    status={status}
                    setStatus={setStatus}
                    setTeacherId={setTeacherId}
                    onClickClose={() => setPopup("filter", "none")}
                    onClickStatus={setFilterStatus}
                    onClickConfirm={requestsFiltering}
                />
            </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Requests;